// Story 048 — Lock endpoint hooks.
//
// Four endpoints (Spec 095):
//   - POST /api/lyse/v1/data-acquisitions/:id/lock/acquire
//   - POST /api/lyse/v1/data-acquisitions/:id/lock/heartbeat
//   - POST /api/lyse/v1/data-acquisitions/:id/lock/release
//   - GET  /api/lyse/v1/data-acquisitions/:id/lock
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";

export interface LockHolderDto {
  userId: string;
  displayName: string;
}

export interface AcquireLockBody {
  phase: "PRE_SCREENING" | "EXPERT_ANALYSIS";
  /**
   * DEFCAT-R2-02 (lyse/v1@7.12.0) — audited takeover of a foreign live
   * lock. When present with a valid `reason` (10–500 chars) the current
   * holder is displaced and the takeover is written to the audit log
   * server-side. Omitted/null for a normal acquire; a reason < 10 chars
   * is rejected with 400.
   */
  takeover?: { reason: string } | null;
}

export interface AcquireLockResponse {
  lockId: string;
  holder: LockHolderDto;
  heldSince: string;
  heartbeatIntervalMs: number;
  idleReleaseMs: number;
}

export interface LockContentionResponse {
  code: "lock_contention";
  heldBy: LockHolderDto;
  heldSince: string;
  idleSince: string;
}

export interface HeartbeatBody {
  lockId: string;
}

export interface HeartbeatResponse {
  holder: LockHolderDto;
  heldSince: string;
  lastHeartbeatAt: string;
}

export interface ReleaseBody {
  lockId: string;
}

export type LockStatusResponse =
  | { status: "free" }
  | {
      status: "held";
      lockId: string;
      holder: LockHolderDto;
      heldSince: string;
      lastHeartbeatAt: string;
    };

export const lockKeys = {
  all: ["lock"] as const,
  forDa: (id: string) => [...lockKeys.all, "da", id] as const,
};

export function useAcquireLockMutation(dataAcquisitionId: string) {
  const api = useApi();
  return useMutation<
    AcquireLockResponse,
    {
      errorId?: string;
      code?: string;
      response?: { status: number; data?: unknown };
    } & Error,
    AcquireLockBody
  >({
    mutationFn: async (body) => {
      const response = await api.post<AcquireLockResponse>(
        `/data-acquisitions/${dataAcquisitionId}/lock/acquire`,
        body,
      );
      return response.data;
    },
  });
}

export function useHeartbeatLockMutation(dataAcquisitionId: string) {
  const api = useApi();
  return useMutation<
    HeartbeatResponse,
    {
      errorId?: string;
      code?: string;
      response?: { status: number; data?: unknown };
    } & Error,
    HeartbeatBody
  >({
    mutationFn: async (body) => {
      const response = await api.post<HeartbeatResponse>(
        `/data-acquisitions/${dataAcquisitionId}/lock/heartbeat`,
        body,
      );
      return response.data;
    },
  });
}

export function useReleaseLockMutation(dataAcquisitionId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<undefined, Error, ReleaseBody>({
    mutationFn: async (body) => {
      await api.post(
        `/data-acquisitions/${dataAcquisitionId}/lock/release`,
        body,
      );
      return undefined;
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: lockKeys.forDa(dataAcquisitionId),
      });
    },
  });
}

/**
 * Polled reader query — returns the current lock state every 30s.
 * Enabled only when the caller is NOT the writer.
 */
export function useLockStatusQuery(args: {
  dataAcquisitionId: string;
  enabled: boolean;
}) {
  const api = useApi();
  return useQuery<LockStatusResponse>({
    queryKey: lockKeys.forDa(args.dataAcquisitionId),
    queryFn: async () => {
      const response = await api.get<LockStatusResponse>(
        `/data-acquisitions/${args.dataAcquisitionId}/lock`,
      );
      return response.data;
    },
    enabled: args.enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}
